import { motion } from "framer-motion";
import { ArrowLeftIcon, ExternalLinkIcon } from "lucide-react";

import { fadeSlideVariants, springGentle } from "./constants";
import { SuccessCheckmark } from "./SuccessCheckmark";
import { UrlBar } from "./UrlBar";

import { Button } from "@/components/ui/button";
import { DialogClose, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface CreatedViewProps {
  shareUrl: string;
  hasExistingSessions: boolean;
  onManage: () => void;
  isPublic?: boolean;
}

export const CreatedView = ({
  shareUrl,
  hasExistingSessions,
  onManage,
  isPublic = true,
}: CreatedViewProps) => (
  <motion.div
    key="created"
    variants={fadeSlideVariants}
    initial="initial"
    animate="animate"
    exit="exit"
    transition={springGentle}
    className="flex min-w-0 flex-col overflow-hidden"
  >
    <div className="flex flex-col items-center px-6 pb-4 pt-8">
      <SuccessCheckmark />
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.15 }}
        className="mt-4 text-center"
      >
        <DialogHeader className="items-center">
          <DialogTitle className="text-base">Link created</DialogTitle>
          <DialogDescription className="mt-1">
            {isPublic
              ? "Anyone with the link can use this."
              : "Only people in your org can use this."}
          </DialogDescription>
        </DialogHeader>
      </motion.div>
    </div>

    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.22, duration: 0.15 }}
      className="px-6 pb-2"
    >
      <UrlBar url={shareUrl} />
    </motion.div>

    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.28, duration: 0.15 }}
      className="mt-2 flex items-center justify-between border-t border-gray-100 px-6 py-3 dark:border-gray-800"
    >
      {/* Left slot — back navigation when there are other links to return to */}
      {hasExistingSessions ? (
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-[13px] text-muted-foreground hover:text-foreground"
          onClick={onManage}
        >
          <ArrowLeftIcon className="h-3.5 w-3.5" />
          Manage Links
        </Button>
      ) : (
        <div />
      )}

      {/* Right slot — open in new tab + close */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-gray-500 hover:text-gray-700"
          onClick={() => window.open(shareUrl, "_blank")}
        >
          <ExternalLinkIcon className="h-3.5 w-3.5" />
          Open
        </Button>
        <DialogClose asChild>
          <Button size="sm" className="rounded-lg px-5">
            Done
          </Button>
        </DialogClose>
      </div>
    </motion.div>
  </motion.div>
);
